// 우측 파일 미리보기 패널 — 결과 패널(ResultsPanel)과 같은 자리(.main 2열)·
// 같은 폭 체계를 SidePanel 셸로 공유한다 (va_rp_w 드래그, 기본 min(680px,46vw)).
// PDF 는 libViewUrl(report-view inline → 브라우저 내장 뷰어), 엑셀/CSV 는
// libExcelViewUrl(서버 HTML 렌더)을 iframe 으로 띄운다. 그 외 형식은 안내 + 다운로드.
// 통합 우측 패널의 '파일 미리보기' 탭 내용 — 탭 바(tabs)는 ChatPage 가 주입한다.
import { type ReactNode } from "react";
import {
  libDownload,
  libPreviewUrl,
  libViewUrl,
} from "../../lib/library";
import { SidePanel, PanelClose } from "../../ui/SidePanel";
import { Button } from "../../ui/Button";

export interface PreviewFile {
  url: string;
  name: string;
  /** library.ts libKindOf 판별 결과 — report(PDF)/excel(엑셀·CSV)/ppt/file */
  kind: string;
}

interface Props {
  file: PreviewFile;
  onClose: () => void;
  /** 너비 드래그 중 상태 — .chat-root.rp-resizing (커서·가이드라인, 결과 패널과 공용) */
  onResizingChange: (on: boolean) => void;
  /** 통합 패널 탭 바 — 결과·미리보기 둘 다 있을 때만 ChatPage 가 내려준다 */
  tabs?: ReactNode;
}

const KIND_LABEL: Record<string, string> = {
  report: "PDF 문서", excel: "엑셀 · CSV", ppt: "프레젠테이션",
};

export default function FilePreviewPanel({ file, onClose, onResizingChange, tabs }: Props) {
  const src = libPreviewUrl(file.kind, file.url);
  // 새 탭 = 미리보기와 같은 보기 경로(엑셀은 HTML 렌더, PDF 는 inline) — 없으면 원본 URL
  const tabUrl = src || libViewUrl(file.url);

  return (
    <SidePanel
      label="파일 미리보기"
      className="file-preview-panel"
      tabs={tabs}
      titles={
        <>
          <div className="rp-title fp-name" title={file.name}>{file.name}</div>
          <div className="rp-sub">{KIND_LABEL[file.kind] || "파일"} 미리보기</div>
        </>
      }
      actions={
        <div className="fp-acts">
          <Button
            variant="icon"
            title="다운로드"
            aria-label="다운로드"
            onClick={() => void libDownload(file.url, file.name || "document")}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
          </Button>
          <Button
            variant="icon"
            title="새 탭에서 열기"
            aria-label="새 탭에서 열기"
            onClick={() => window.open(tabUrl, "_blank", "noopener")}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <path d="M15 3h6v6" />
              <path d="M10 14L21 3" />
            </svg>
          </Button>
          <PanelClose label="미리보기 닫기" onClick={onClose} />
        </div>
      }
      onResizingChange={onResizingChange}
    >
      {src ? (
        <iframe className="fp-frame" src={src} title={file.name} />
      ) : (
        <div className="fp-unsupported">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <path d="M14 2v6h6" />
          </svg>
          <p>미리보기를 지원하지 않는 형식입니다.</p>
          <button
            className="rp-excel"
            type="button"
            onClick={() => void libDownload(file.url, file.name || "document")}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
            다운로드
          </button>
        </div>
      )}
    </SidePanel>
  );
}
